import { AlertColor } from "@mui/material";
import { cloneDeep } from "lodash";
import { Toast } from "../models";
import preval from "preval.macro";
import moment from "moment";

// use this to typescript safe check property name of a type
export const pn = <TObject>(key: keyof TObject): string => {
  return key.toString();
};

export const stringLiteral = <TObject>(value: TObject): string => {
  return value as unknown as string;
};

export const deepCopy = <T>(obj: T): T => {
  return cloneDeep(obj) as T;
};

export const getAppUpdatedDate = (): string | undefined => {
  return preval`module.exports = new Date().toLocaleString()`;
};

export const isNullOrEmpty = (value: any): boolean => {
  if (value === undefined || value === null) return true;

  if (Array.isArray(value) && (value as []).length === 0) return true;

  return false;
};

export const toCamelCase = (input: string | undefined): string | undefined => {
  if (!input) return input;
  return input.charAt(0).toLowerCase() + input.slice(1);
};

export const getDelimitedString = (
  stringArray: string[] | undefined,
  delimiter: string,
  lastDelimiter?: string
): string | undefined => {
  if (stringArray && stringArray.length > 0) {
    let sentence: string = "";
    for (let i = 0; i < stringArray.length; i++) {
      if (i > 0) {
        sentence = sentence + delimiter;

        // if (lastDelimiter && i === stringArray.length - 1) {
        //   sentence = sentence + " " + lastDelimiter + " ";
        // } else {
        //   sentence = sentence + delimiter;
        // }
      }

      sentence = sentence + stringArray[i];
    }
    return sentence;
  }

  return undefined;
};

// turns firstName into First Name
const RESERVED_WORDS = ["of", "and", "or", "the"]; // Add more words as needed
export const formatPropertyName = (propertyName: string | undefined): string | undefined => {
  if (propertyName) {
    // Split the input string by capital letters and join the resulting array with spaces
    const words = propertyName.split(/(?=[A-Z])/).join(" ");

    // Capitalize the first letter of each word
    const capitalizedWords = words.replace(/\b\w/g, (char) => char.toUpperCase());

    // Convert reserved words to lowercase
    const formattedWords = capitalizedWords.replace(new RegExp(`\\b(${RESERVED_WORDS.join("|")})\\b`, "gi"), (word) =>
      word.toLowerCase()
    );

    return formattedWords;
  }
  return propertyName;
};

export const formatDate = (value: Date | undefined): string | undefined => {
  if (value !== undefined && value !== null) {
    let momentDate = moment(value);
    return momentDate.format("MM/DD/YYYY");
  }

  return undefined;
};

export const formatDecimal = (value: number | undefined, toFixed: number = 2): string | undefined => {
  if (value !== undefined && value !== null) {
    return value.toFixed(toFixed);
  }

  return undefined;
};

export const formatSchoolYear = (schoolYear: number | undefined): string | undefined => {
  if (schoolYear) {
    return `${schoolYear}/${schoolYear + 1}`;
  }

  return undefined;
};

export const formatBoolean = (bool: boolean | undefined): string | undefined => {
  if (bool !== undefined) {
    if (bool === true) return "Yes";
    if (bool === false) return "No";
  }

  return undefined;
};

export const formatCurrency = (value: number | null | undefined): string | undefined => {
  if (value !== undefined && value !== null) {
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  return undefined;
};

export const formatPercent = (value: number | undefined): string | undefined => {
  if (value !== undefined && value !== null) {
    return `${value.toFixed(2)}%`;
  }

  return undefined;
};

export const formatPercentTotal = (value: number | undefined, total: number | undefined): string | undefined => {
  if (value !== undefined && value !== null && total !== undefined && total !== null && total != 0) {
    var temp = (value / total) * 100;
    return `(${temp.toFixed(2)}%)`;
  }

  return undefined;
};

export const formatSeconds = (seconds: number | undefined): string | undefined => {
  if (seconds !== undefined) {
    const hours = Math.floor(seconds / 3600);
    const remainingSecondsAfterHours = seconds % 3600;
    const minutes = Math.floor(remainingSecondsAfterHours / 60);
    const remainingSeconds = remainingSecondsAfterHours % 60;

    let formattedTime = "";
    if (hours > 0) {
      formattedTime += `${hours} hours`;
      if (minutes > 0 || remainingSeconds > 0) {
        formattedTime += ` and `;
      }
    }
    if (minutes > 0) {
      formattedTime += `${minutes} minutes`;
      if (remainingSeconds > 0) {
        formattedTime += ` and `;
      }
    }
    if (remainingSeconds > 0) {
      formattedTime += `${remainingSeconds} seconds`;
    }

    return formattedTime;
  }

  return undefined;
};

export const formatMinutes = (minutes: number | undefined): string | undefined => {
  if (minutes === undefined || minutes === null) return undefined;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  let formattedTime = "";
  if (hours > 0) {
    formattedTime += `${hours} hr`;
    if (remainingMinutes > 0) {
      formattedTime += ` ${remainingMinutes} min`;
    }
  } else {
    formattedTime = `${remainingMinutes} min`;
  }

  return formattedTime;
};

export const formatDuration = (
  valueString: string | undefined | null,
  toDecimalFormat: boolean = false
): string | undefined => {
  if (valueString === undefined || valueString === null) return undefined;

  let hours: number = 0;
  let minutes: number = 0;

  const value: number | undefined = Number(valueString);
  if (isNaN(value)) {
    // if in hh:mm:ss TimeSpan format
    var timeSpanParts = valueString.split(":");
    if (timeSpanParts[0].indexOf(".") >= 0) {
      // if the hour part is in format 0.00, calculate the total hours
      const timeSpanHoursParts = timeSpanParts[0].split(".");
      hours = parseInt(timeSpanHoursParts[0]) * 24 + parseInt(timeSpanHoursParts[1]);
    } else {
      // else just take the hours as is
      hours = parseInt(timeSpanParts[0]);
    }

    minutes = parseInt(timeSpanParts[1]);
  } //else if in number format: that means its in minutes
  else {
    hours = value / 60;
    minutes = value % 60;
  }

  if (toDecimalFormat === true) {
    return (Math.round((hours + minutes / 60) * 100) / 100).toFixed(2);
  } else {
    if (hours == 0 && minutes == 0) {
      return "0m";
    }

    var returnString = "";

    if (hours > 0) {
      returnString = hours + "h";
    }

    if (minutes > 0) {
      returnString = returnString + " " + minutes + "m";
    }

    return returnString;
  }
};

export const formatTime = (valueString: string | undefined | null): string | undefined => {
  // Check for null or undefined
  if (valueString == null) {
    return undefined;
  }

  // Check for empty string
  if (valueString.trim() === "") {
    return undefined;
  }

  // Use a regular expression to validate the time format (HH:MM:SS)
  const timeFormatRegex = /^(?:2[0-3]|[01]?[0-9]):[0-5][0-9]:[0-5][0-9](?:\.\d+)?$/;
  if (!timeFormatRegex.test(valueString)) {
    throw new Error('Invalid time format. Please use "HH:MM:SS".');
  }

  // Split the time string into its components
  const [hours, minutes, seconds] = valueString.split(":").map(Number);

  // Convert 24-hour time to 12-hour time
  const period = hours >= 12 ? "PM" : "AM";
  const convertedHours = hours % 12 || 12; // Convert "00" hours to "12"

  // Return the formatted time string
  return `${convertedHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
};

export const formatPhoneNumber = (phoneNumber: string | undefined | null): string | undefined | null => {
  if (!phoneNumber) return phoneNumber;

  // Remove all non-numeric characters
  const sanitizedPhoneNumber = phoneNumber.replace(/\D/g, '');

  if (sanitizedPhoneNumber.length > 3 && sanitizedPhoneNumber.length < 7) {
    const areaCode = sanitizedPhoneNumber.slice(0, 3);
    const numberPart1 = sanitizedPhoneNumber.slice(3, 6);
    return `${areaCode}-${numberPart1}`;
  }

  if (sanitizedPhoneNumber.length >= 7 && sanitizedPhoneNumber.length <= 10) {
    const areaCode = sanitizedPhoneNumber.slice(0, 3);
    const numberPart1 = sanitizedPhoneNumber.slice(3, 6);
    const numberPart2 = sanitizedPhoneNumber.slice(6);
    return `${areaCode}-${numberPart1}-${numberPart2}`;
  }

  return sanitizedPhoneNumber;
};

export const formatAlphaOnly = (tempString: string | undefined | null): string | undefined | null => {
  if (!tempString) return tempString;

    // Regular expression to match only letters and special characters
    let regex = /^[a-zA-Z\W]*$/; // \W matches any non-word character (special characters)

    if (!regex.test(tempString)) {
      // If the input contains numbers, remove them
      tempString = tempString.replace(/[0-9]/g, "");
    }

  return tempString;
};

export const formatFileSize = (value: number | undefined) => {
  if (value) {
    if (value >= 0 && value < 1000) {
      return value.toFixed(2) + " " + "B";
    } else if (value >= 1000 && value < 1000000) {
      return (value / 1000).toFixed(2) + " " + "KB";
    } else if (value >= 1000000 && value < 1000000000) {
      return (value / 1000000).toFixed(2) + " " + "MB";
    }
  }

  return value;
};

// export const formatSeconds = (value: number): string => {
//   return new Date(value * 1000).toISOString().substr(14, 5);
// };

const imgExtensions = ["jpg", "jpeg", "gif", "bmp", "tiff", "png"];
export const isFilenameImage = (filename: string | undefined): boolean => {
  if (filename) {
    for (var i in imgExtensions) {
      if (filename.toLowerCase().endsWith("." + imgExtensions[i])) {
        return true;
      }
    }
  }

  return false;
};

export const newToast = (alertColor: AlertColor, message?: string): Toast => {
  let toast: Toast = {} as Toast;

  toast.alertColor = alertColor;

  if (message) toast.message = message;
  else {
    if (toast.alertColor === "success") toast.message = "Action Successful";
    if (toast.alertColor === "error") toast.message = "An error has occured";
  }

  toast.timeSent = new Date().getTime();

  return toast;
};

export const offsetListItems = (listItems: any[], offset: number): any[] => {
  return listItems.slice(0, offset);
};

export const sortListItems = (listItems: any[], sortProperty: string, sortDesc: boolean): any[] => {
  return !sortDesc
    ? listItems.sort((a, b) => {
        if (a[sortProperty] < b[sortProperty]) {
          return -1;
        }
        if (a[sortProperty] > b[sortProperty]) {
          return 1;
        }
        return 0;
      })
    : listItems.sort((a, b) => {
        if (a[sortProperty] < b[sortProperty]) {
          return 1;
        }
        if (a[sortProperty] > b[sortProperty]) {
          return -1;
        }
        return 0;
      });
};

export const openFile = (
  fileName: string | undefined,
  base64String: string | undefined,
  contentType: string | undefined
) => {
  const binaryData = atob(base64String!);
  const bytes = new Uint8Array(binaryData.length);

  for (let i = 0; i < binaryData.length; i++) {
    bytes[i] = binaryData.charCodeAt(i);
  }

  const url = window.URL.createObjectURL(new Blob([bytes], { type: contentType }));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName!);
  document.body.appendChild(link);
  link.click();
};

function replaceSpacesWithUnderscores(fileName) {
  return fileName.replace(/\s+/g, '_');
}